import { CommonTokenScopes } from '@marpla/api-sdk';
import { fromBase64 } from '@marpla/core/function/base64.function';

export enum TokenScopeIds {
  CONTENT = 2,
  ANALYTICS = 4,
  PRICES_AND_DISCOUNTS = 8,
  MARKETPLACE = 16,
  STATISTICS = 32,
  PROMOTION = 64,
  QUESTIONS_AND_FEEDBACKS = 128,
  RECOMMENDATIONS = 256,
}

export interface WbJwtPayload {
  ent: number;
  exp: number;
  id: number;
  iid: number;
  oid: number;
  s: number;
  sid: number;
  uid: number;
}

const WB_TOKEN_READONLY = 1 << 30;

type DecodedTokenScope = CommonTokenScopes | 'recommendations';

const TOKEN_SCOPE_BY_ID: Record<TokenScopeIds, DecodedTokenScope> = {
  [TokenScopeIds.CONTENT]: CommonTokenScopes.CONTENT,
  [TokenScopeIds.ANALYTICS]: CommonTokenScopes.ANALYTICS,
  [TokenScopeIds.PRICES_AND_DISCOUNTS]: CommonTokenScopes.PRICES,
  [TokenScopeIds.MARKETPLACE]: CommonTokenScopes.MARKETPLACE,
  [TokenScopeIds.STATISTICS]: CommonTokenScopes.STATISTICS,
  [TokenScopeIds.PROMOTION]: CommonTokenScopes.PROMOTION,
  [TokenScopeIds.QUESTIONS_AND_FEEDBACKS]: CommonTokenScopes.FEEDBACKS,
  [TokenScopeIds.RECOMMENDATIONS]: 'recommendations',
};

function compareBytes(value: number, scopeId: number): boolean {
  return (value & scopeId) > 0;
}

function decodeByteCodeToScope(byteCode: number): DecodedTokenScope[] {
  const scopes: DecodedTokenScope[] = [];

  Object.values(TokenScopeIds).forEach((scopeId) => {
    const scopeIdNumber = Number(scopeId) as TokenScopeIds;

    if (!Number.isNaN(scopeIdNumber) && compareBytes(byteCode, scopeIdNumber)) {
      scopes.push(TOKEN_SCOPE_BY_ID[scopeIdNumber]);
    }
  });

  return scopes;
}

export function validateTokenFormat(token: string): boolean | string {
  try {
    JSON.parse(fromBase64(token.split('.')[1]));

    return true;
  } catch {
    return 'Неверный формат ключа';
  }
}

export function validateReadToken(token: string): boolean | string {
  const tokenPayload = JSON.parse(fromBase64(token.split('.')[1])) as WbJwtPayload;

  const scope = tokenPayload.s;

  if (scope > WB_TOKEN_READONLY) {
    return 'Токен не должен быть только для чтения';
  }

  return true;
}

export function validateToken(
  token: string,
  requiredScopes: CommonTokenScopes[] = [
    CommonTokenScopes.PROMOTION,
    CommonTokenScopes.CONTENT,
    CommonTokenScopes.ANALYTICS,
  ],
): boolean | string {
  const tokenPayload = JSON.parse(fromBase64(token.split('.')[1])) as WbJwtPayload;

  const scope = tokenPayload.s;

  const scopes = decodeByteCodeToScope(scope);

  let isValid = true;
  let errorText = '';

  const scopeNames = {
    [CommonTokenScopes.CONTENT]: 'Контент',
    [CommonTokenScopes.PRICES]: 'Цены и скидки',
    [CommonTokenScopes.PROMOTION]: 'Продвижение',
    [CommonTokenScopes.ANALYTICS]: 'Аналитика',
    [CommonTokenScopes.FEEDBACKS]: 'Вопросы и отзывы',
  };

  requiredScopes.forEach((s) => {
    if (!scopes.includes(s)) {
      errorText += `${scopeNames[s]} `;
      isValid = false;
    }
  });

  return isValid || errorText;
}

export enum ConnectApiTokenErrors {
  BAD_SERVICE_TOKEN = 'Принимаются только токены типов «Базовый» или «Персональный». Так же персональные токены от других сервисов не поддерживаются.',
  CANNOT_VERIFY_ADVERT_TOKEN_UNABLE_TO_FIND_ARTICLE = 'По данному API-ключу нет ни одной созданной кампании. Создайте рекламную кампанию в кабинете WB и повторите попытку',
  CANNOT_VERIFY_ADVERT_TOKEN_UNABLE_TO_FIND_ACCOUNT = 'По данному API-ключу нет ни одного поставщика',
  'User already have this token' = 'У Вас уже добавлен такой токен',
  'Bad props: advertToken must match /^[\\w-]*\\.[\\w-]*\\.[\\w-]*$/ regular expression' = 'Введен не валидный токен',
  USER_WITH_THIS_TOKEN_ALREADY_EXISTS = 'Пользователь с этим токеном уже существует',
  UNAUTHORIZED = 'Ключ недействителен',
  SCOPE_HAS_RESTRICTED_SECTIONS = 'API ключ имеет лишние категории. Создайте ключ содержащий исключительно те категории, которые указаны в инструкции.',
  INTERNAL_SERVER_ERROR = 'API ключ не валиден. Создайте новый ключ',
  TOKEN_SHOULD_HAVE_ALL_SCOPES = 'Ключ имеет недостающие категории. Создайте ключ со всеми требуемыми категориями.',
  TOKEN_SHOULD_BE_PERSONAL_OR_BASIC = 'Ключ должен быть типа "Персональный" или "Базовый"',
  UNKNOWN = 'Произошла ошибка. Обратитесь в тех. поддержку',
}

export const DEFAULT_CATEGORIES = [
  CommonTokenScopes.CONTENT,
  CommonTokenScopes.ANALYTICS,
  CommonTokenScopes.PROMOTION,
] as const;

export const SALES_CATEGORIES = [
  CommonTokenScopes.CONTENT,
  CommonTokenScopes.ANALYTICS,
  CommonTokenScopes.MARKETPLACE,
  CommonTokenScopes.PROMOTION,
  CommonTokenScopes.STATISTICS,
  CommonTokenScopes.PRICES,
  CommonTokenScopes.SUPPLIES,
  CommonTokenScopes.FINANCES,
] as const;

export const UI_TOKEN_SCOPES = [
  CommonTokenScopes.CONTENT,
  CommonTokenScopes.ANALYTICS,
  CommonTokenScopes.MARKETPLACE,
  CommonTokenScopes.PROMOTION,
  CommonTokenScopes.STATISTICS,
  CommonTokenScopes.PRICES,
  CommonTokenScopes.SUPPLIES,
  CommonTokenScopes.FINANCES,
] as const;

export type UiTokenScope = (typeof UI_TOKEN_SCOPES)[number];
export const CATEGORY_INFO = {
  [CommonTokenScopes.CONTENT]: {
    label: 'Контент',
    tooltip: 'Информация о карточках товаров и о размерах.',
  },
  [CommonTokenScopes.ANALYTICS]: {
    label: 'Аналитика',
    tooltip: 'Воронка продаж.\nПоисковые запросы.',
  },
  [CommonTokenScopes.MARKETPLACE]: {
    label: 'Маркетплейс',
    tooltip: 'Сборочные задания и остатки ФБС, ДБС, ДБВ и самовывоз.',
  },
  [CommonTokenScopes.PROMOTION]: {
    label: 'Продвижение',
    tooltip: 'Информация о кампаниях.\nУправление кампаниями и рекламой.',
  },
  [CommonTokenScopes.STATISTICS]: {
    label: 'Статистика',
    tooltip: 'Заказы и продажи.\nОтчёт о продажах по реализации.\nОстатки ФБО.',
  },
  [CommonTokenScopes.PRICES]: {
    label: 'Цены и скидки',
    tooltip: 'Информация о ценах и скидках.\nИзменение цен и скидок.\nКалендарь акций.',
  },
  [CommonTokenScopes.SUPPLIES]: {
    label: 'Поставки',
    tooltip: 'Информация о поставках ФБО.',
  },
  [CommonTokenScopes.FINANCES]: {
    label: 'Финансы',
    tooltip: 'Для получения отчетов и данных.',
  },
} satisfies Record<UiTokenScope, { label: string; tooltip: string }>;
